import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { map, take, switchMap, filter, distinctUntilChanged } from 'rxjs/operators';
import { constants } from '../shared/constants/constants';

export interface WhatsNewFeature {
    title: string;
    description: string;
    date: string;
    version: string;
    requiresBox2: boolean;
}

interface WhatsNewConfig {
    [version: string]: {
        features: WhatsNewFeature[];
    };
}

@Injectable({
    providedIn: 'root',
})
export class WhatsNewService {
    private readonly appVersion = environment.appVersion;
    private readonly storageKey = `whats-new-${this.appVersion}`;
    private showBannerSubject = new BehaviorSubject<boolean>(false);
    private deviceSubject = new BehaviorSubject<string>(null);
    private featuresSubject = new BehaviorSubject<WhatsNewFeature[]>([]);

    showBanner$ = combineLatest([
        this.showBannerSubject,
        this.deviceSubject,
        this.featuresSubject,
    ]).pipe(
        map(([showBanner, device, features]) => {
            return showBanner && device && features.length > 0;
        }),
        distinctUntilChanged()
    );

    constructor(private http: HttpClient) {
        const wasDismissed = localStorage.getItem(this.storageKey) === 'true';
        if (wasDismissed) {
            this.showBannerSubject.next(false);
        }
    }

    updateDevice(device: string): void {
        if (!device) {
            this.deviceSubject.next(null);
            this.showBannerSubject.next(false);
            return;
        }
        this.deviceSubject.next(device);
        this.checkForNewFeatures();
    }

    checkForNewFeatures(): void {
        this.getFeatures()
            .pipe(take(1))
            .subscribe((features) => {
                this.featuresSubject.next(features);
                const wasDismissed = localStorage.getItem(this.storageKey) === 'true';
                this.showBannerSubject.next(features.length > 0 && !wasDismissed);
            });
    }

    getFeatures(): Observable<WhatsNewFeature[]> {
        return this.http.get<WhatsNewConfig>('assets/config/whats-new.json').pipe(
            switchMap((config) => {
                const currentVersionFeatures = config[this.appVersion]?.features || [];
                return this.deviceSubject.pipe(
                    filter((device) => !!device), // Only proceed if we have a device
                    map((device) => {
                        const isBox2User = [
                            constants.application.devices.smart_box,
                            constants.application.devices.smart_box2,
                        ].includes(device);
                        let filteredFeatures = currentVersionFeatures;
                        if (!isBox2User) {
                            filteredFeatures = currentVersionFeatures.filter(
                                (feature) => !feature.requiresBox2
                            );
                        }
                        return filteredFeatures.map((feature) => ({
                            ...feature,
                            version: this.appVersion,
                        }));
                    }),
                    distinctUntilChanged()
                );
            })
        );
    }

    dismissBanner(): void {
        localStorage.setItem(this.storageKey, 'true');
        this.showBannerSubject.next(false);
    }

    getCurrentVersion(): string {
        return this.appVersion;
    }
}
